import { getClient } from "src/api/api.ts";
import { HttpError } from "src/api/httpError.ts";
import { components } from "src/api/schema";
import { PageAndSortRequest, PageRequest, SortRequest } from "src/model/pageRequest.ts";
import { PaginatedResponse } from "src/model/paginatedResponse.ts";
import { ReportRequest } from "src/model/reportRequest.ts";
import {
  SimpleSubscription,
  Subscription,
  SubscriptionFilterOptions,
  SubscriptionFilterProps,
  SubscriptionPosition,
  SubscriptionSortBy,
} from "src/model/subscription";
import { ShipmentRegistrationLog } from "src/model/subscriptionLog.ts";
import { SubscriptionCreateRequest, SubscriptionUpdateRequest } from "src/model/subscriptionSaveRequest.ts";
import { addOneDay } from "src/utils/datetime.ts";

const mapFilter = (filter: SubscriptionFilterProps): components["schemas"]["SubscriptionFilterIn"] => {
  return {
    query: filter.query,
    companyId: filter.companyId,
    customerId: filter.customerId,
    excludeFailedSubscriptions: filter.excludeFailedSubscriptions,
    scacs: filter.scacs,
    portOfDischargeUnlocodes: filter.portOfDischargeUnlocodes,
    portOfLoadingUnlocodes: filter.portOfLoadingUnlocodes,
    etaPortOfDischargeFrom: filter.etaPortOfDischarge?.at(0)?.getTime(),
    etaPortOfDischargeTo: addOneDay(filter.etaPortOfDischarge?.at(1)?.getTime()),
    status: filter.status,
  };
};

export const fetchSubscription = async (subscriptionId: string): Promise<SimpleSubscription> => {
  const {
    data,
    error,
    response: { status },
  } = await getClient().GET("/API/iSEA5/V2.1/Subscriptions/{subscriptionId}", {
    params: {
      path: {
        subscriptionId: subscriptionId,
      },
    },
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }

  if (data !== undefined) {
    return data;
  }

  throw new Error("Failed to fetch subscription");
};

export const fetchSubscriptions = async (
  filter: SubscriptionFilterProps,
  pagination: PageAndSortRequest<SubscriptionSortBy>
): Promise<PaginatedResponse<Subscription>> => {
  const {
    data,
    error,
    response: { status },
  } = await getClient().POST("/API/iSEA5/V2.1/Subscriptions/Search", {
    params: {
      query: {
        limit: pagination.limit,
        offset: pagination.offset,
        sortBy: pagination.sortBy,
        sortDir: pagination.sortDir,
      },
    },
    body: mapFilter(filter),
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }

  if (data !== undefined) {
    return data;
  }

  throw new Error("Failed to fetch subscriptions");
};

export const fetchSubscriptionFilterOptions = async (
  filter: SubscriptionFilterProps
): Promise<SubscriptionFilterOptions> => {
  const {
    data,
    error,
    response: { status },
  } = await getClient().POST("/API/iSEA5/V2.1/Subscriptions/FilterOptions", {
    body: mapFilter(filter),
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }

  if (data !== undefined) {
    return data;
  }

  throw new Error("Failed to fetch subscription filter");
};

export const fetchSubscriptionPositions = async (filter: SubscriptionFilterProps): Promise<SubscriptionPosition[]> => {
  const {
    data,
    error,
    response: { status },
  } = await getClient().POST("/API/iSEA5/V2.1/SubscriptionPositions", {
    body: mapFilter(filter),
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }

  if (data !== undefined) {
    return data;
  }

  throw new Error("Failed to fetch subscriptions");
};

export const fetchSubscriptionLogs = async (
  query: string | undefined,
  pagination: PageRequest
): Promise<PaginatedResponse<ShipmentRegistrationLog>> => {
  const {
    data,
    error,
    response: { status },
  } = await getClient().GET("/API/iSEA5/V2.1/SubscriptionLogs", {
    params: { query: { ...pagination, query: query } },
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }

  if (data !== undefined) {
    return data;
  }

  throw new Error("Failed to fetch subscription logs");
};

export const fetchSubscriptionReportRequest = async (
  filter: SubscriptionFilterProps,
  sorting: SortRequest<SubscriptionSortBy>
): Promise<ReportRequest> => {
  const {
    data,
    error,
    response: { status },
  } = await getClient().POST("/API/iSEA5/V2.1/Subscriptions/Reports", {
    params: {
      query: {
        sortBy: sorting.sortBy,
        sortDir: sorting.sortDir,
      },
    },
    body: mapFilter(filter),
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }

  if (data !== undefined) {
    return data;
  }

  throw new Error("Failed to fetch subscription report request");
};

export const createSubscription = async (subscriptionSaveRequest: SubscriptionCreateRequest): Promise<void> => {
  const {
    error,
    response: { status },
  } = await getClient().POST("/API/iSEA5/V2.1/Subscriptions", {
    body: subscriptionSaveRequest,
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }
};

export const updateSubscription = async (
  subscriptionId: string,
  subscriptionUpdateRequest: SubscriptionUpdateRequest
): Promise<void> => {
  const {
    error,
    response: { status },
  } = await getClient().PUT("/API/iSEA5/V2.1/Subscriptions/{subscriptionId}", {
    params: {
      path: {
        subscriptionId: subscriptionId,
      },
    },
    body: subscriptionUpdateRequest,
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }
};

export const retrySubscriptionRegistration = async (subscriptionId: string): Promise<void> => {
  const {
    error,
    response: { status },
  } = await getClient().POST("/API/iSEA5/V2.1/Subscriptions/{subscriptionId}/RetryRegistration", {
    params: {
      path: {
        subscriptionId: subscriptionId,
      },
    },
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }
};

export const deleteSubscription = async (subscriptionId: string): Promise<void> => {
  const {
    error,
    response: { status },
  } = await getClient().DELETE("/API/iSEA5/V2.1/Subscriptions/{subscriptionId}", {
    params: {
      path: {
        subscriptionId: subscriptionId,
      },
    },
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }
};
